import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { map, Observable } from 'rxjs';

import { AuthService } from './auth.service';

/**
 * Service for checking user roles and groups from Azure AD token claims.
 *
 * Supports both:
 * - App Roles: Defined in Azure AD app registration (roles claim)
 * - Security Groups: Azure AD groups (groups claim or groupMembershipClaims)
 *
 * To configure in Azure AD:
 * 1. For App Roles: App Registration > App roles > Create roles and assign to users/groups
 * 2. For Groups: App Registration > Token configuration > Add optional claim > groups
 *    - Choose "Security groups" or "All groups" based on your needs
 */
@Injectable({
	providedIn: 'root'
})
export class RoleService {
	constructor(private authService: AuthService) {}

	/**
	 * Get all roles/groups for the current user
	 */
	getUserRoles(): string[] {
		// Get current user synchronously from auth service
		const currentUser = this.authService.getCurrentUser();

		if (!currentUser) {
			return [];
		}

		return this.extractRolesFromAccount(currentUser as any);
	}

	/**
	 * Get user roles as an observable
	 */
	getUserRoles$(): Observable<string[]> {
		return this.authService.user$.pipe(
			map(authResult => {
				if (!authResult || !authResult.account) {
					return [];
				}
				return this.extractRolesFromAccount(authResult.account);
			})
		);
	}

	/**
	 * Check if user has a specific role
	 */
	hasRole(role: string): boolean {
		const roles = this.getUserRoles();
		return roles.includes(role);
	}

	/**
	 * Check if user has any of the specified roles
	 */
	hasAnyRole(roles: string[]): boolean {
		if (!roles || roles.length === 0) {
			return true;
		}
		const userRoles = this.getUserRoles();
		return roles.some(role => userRoles.includes(role));
	}

	/**
	 * Check if user has all of the specified roles
	 */
	hasAllRoles(roles: string[]): boolean {
		if (!roles || roles.length === 0) {
			return true;
		}
		const userRoles = this.getUserRoles();
		return roles.every(role => userRoles.includes(role));
	}

	/**
	 * Extract roles from account token claims
	 * Handles both App Roles (roles claim) and Security Groups (groups claim)
	 */
	private extractRolesFromAccount(account: AccountInfo): string[] {
		const roles: string[] = [];

		// Extract App Roles from the 'roles' claim
		// These are defined in Azure AD App Registration > App roles
		if (account.idTokenClaims && account.idTokenClaims['roles']) {
			const appRoles = account.idTokenClaims['roles'] as string[];
			roles.push(...appRoles);
		}

		// Extract Security Groups from the 'groups' claim
		// These require optional claims to be configured in Azure AD
		if (account.idTokenClaims && account.idTokenClaims['groups']) {
			const groups = account.idTokenClaims['groups'] as string[];
			roles.push(...groups);
		}

		// Also check access token claims if available
		// Note: You may need to decode the access token separately if needed
		// For now, we'll rely on ID token claims which are more commonly used

		return roles;
	}

	/**
	 * Get roles from a specific account (useful for checking other users)
	 */
	getRolesFromAccount(account: AccountInfo): string[] {
		return this.extractRolesFromAccount(account);
	}
}
